import { HeaderWrapper } from 'layouts';
import type { GroupDetail } from 'models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { canViewAnnouncements } from 'permissions';
import { Card, HeaderAsset, HeaderContent, Tabs } from 'ui';
import { images } from 'common/utils';
import { useGetGroupPreviewIcon, useGetGroupPreviewTypeName } from '../hooks';
import type { GroupDetailScope } from '../types';

type P = {
  group: GroupDetail;
  navigateToGroupDetailPageByKey: ({ id, key }: { id: string; key?: GroupDetailScope }) => Promise<void>;
  scope: GroupDetailScope;
};

const GroupDetailHeader = ({ group, navigateToGroupDetailPageByKey, scope }: P) => {
  const { t } = useTranslation();
  const getGroupPreviewTypeName = useGetGroupPreviewTypeName();
  const getGroupPreviewIcon = useGetGroupPreviewIcon();

  const items = useMemo(() => {
    const tabs = [];

    if (canViewAnnouncements({ group })) {
      tabs.push({ key: 'announcements', label: t('Announcements') });
    }

    tabs.push({ key: 'about', label: t('About') });

    return tabs.length > 1 ? tabs : undefined;
  }, [group, t]);

  return (
    <>
      <HeaderWrapper
        left={
          <HeaderAsset
            image={{ alt: t('Group cover photo'), src: group.coverPhoto?.url ?? images.default.groupCoverPhoto }}
          />
        }
        right={
          <HeaderContent
            membersCount={group.membersCount}
            tags={{
              children: getGroupPreviewTypeName({ groupType: group.groupType }),
              icon: getGroupPreviewIcon({ groupType: group.groupType }),
            }}
            title={group.name}
          />
        }
      />
      {items && items.length > 1 ? (
        <Card shrinkedBottom shrinkedTop>
          <Tabs
            activeKey={scope}
            hasContentHolderHidden
            items={items}
            onTabClick={async (selectedTab) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
              await navigateToGroupDetailPageByKey({ id: group.id, key: selectedTab as GroupDetailScope });
            }}
          />
        </Card>
      ) : null}
    </>
  );
};

export default GroupDetailHeader;
